import { Link } from "react-router-dom";
import { Scales, ArrowLeft } from "@phosphor-icons/react";
import { BUSINESS } from "@/data/site";

// Standard Terms of Service for a home service dispatch business. Written for
// U.S. jurisdiction (Louisiana) and Google Play Store compliance.
const LAST_UPDATED = "July 10, 2026";
const CONTACT_EMAIL = "david.josephindustries@gmail.com";
const GOVERNING_STATE = "Louisiana";

export default function TermsPage() {
  return (
    <div data-testid="terms-page">
      <section className="ji-container pt-16 md:pt-24 pb-8">
        <Link to="/" data-testid="terms-back-home" className="text-xs uppercase tracking-[0.22em] text-[var(--ji-muted)] hover:text-[var(--ji-primary)] inline-flex items-center gap-1 mb-6">
          <ArrowLeft size={12} weight="bold" /> Back to home
        </Link>
        <div className="ji-overline mb-5 inline-flex items-center gap-2">
          <Scales size={12} weight="fill" /> Legal
        </div>
        <h1 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }} className="text-5xl md:text-6xl font-bold tracking-tight">
          Terms of Service
        </h1>
        <p className="mt-5 text-sm text-[var(--ji-muted)]">Last updated: {LAST_UPDATED}</p>
      </section>

      <section className="ji-container pb-24 max-w-3xl">
        <article className="prose prose-neutral prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-3 prose-p:text-[var(--ji-text)] prose-p:leading-relaxed text-[var(--ji-text)] space-y-6">
          <p className="text-base leading-relaxed">
            These Terms of Service ("Terms") govern your access to and use of the {BUSINESS.name}
            website at josephindustries.site and any related mobile applications (the "Service"),
            and the home services we provide. By using the Service or requesting a job, you agree
            to these Terms.
          </p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>1. Who We Are</h2>
          <p>{BUSINESS.name} is a licensed home service dispatch company based in Southern Louisiana.
          We provide appliance repair, appliance installation, courier &amp; delivery, post-construction
          cleanup, junk removal, furniture assembly, furniture moving, and heavy lifting to residential
          and commercial customers.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>2. Service Requests &amp; Quotes</h2>
          <p>Submitting a quote or booking request does not constitute a binding contract until we
          confirm the appointment by phone, text, or email. Prices displayed on the Service are
          estimates; the final price is determined at the job site after physical assessment. We reserve
          the right to decline any job.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>3. Pricing &amp; Payment</h2>
          <ul className="list-disc pl-6 space-y-1 text-sm leading-relaxed">
            <li><strong>Diagnostic fee:</strong> $79 standard, $119 emergency or long-distance. Credited toward the repair total if you approve the repair.</li>
            <li><strong>Deposits:</strong> Some jobs require a deposit to hold the appointment. Deposits are credited toward the final invoice.</li>
            <li><strong>Payment methods:</strong> All payments are processed by PayPal (LIVE mode). A 5% card processing surcharge is applied to all card payments.</li>
            <li><strong>Parts orders:</strong> When you order a part through us, we bill an invoice that includes a 25% service markup over the retail price plus any applicable local pickup fee. This funds our sourcing, delivery, and free install-reschedule guarantee.</li>
          </ul>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>4. Cancellations &amp; Refunds</h2>
          <p>You may cancel a scheduled appointment up to <strong>24 hours in advance</strong> for a full
          refund of any deposit. Cancellations within 24 hours forfeit 50% of the deposit to cover
          reserved crew time. No-shows forfeit the full deposit. Diagnostic fees are non-refundable once
          the technician has arrived on site.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>5. Warranty &amp; Guarantee</h2>
          <p>We warrant our labor for <strong>30 days</strong> from the completion date. If the same repair
          fails within 30 days due to our workmanship, we will return and remediate at no additional labor
          cost. Parts are covered by the manufacturer's warranty only. This warranty does not cover damage
          caused by misuse, unrelated failures, or subsequent third-party service.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>6. Referral Program</h2>
          <p>Our "Refer &amp; Earn" program grants $25 credit to both the referrer and the new customer
          after we successfully complete the new customer's paid job. Credits have no cash value, are not
          transferable, expire 12 months after issue, and cannot be combined with the 10% shop-buyer
          discount on the same job. We reserve the right to modify or terminate the program at any time.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>7. Affiliate Links &amp; Amazon Products</h2>
          <p>Some products in our shop link to third-party retailers (primarily Amazon). We may earn a
          small commission on qualifying purchases at no additional cost to you. We are not the seller of
          those products and are not responsible for their fulfillment, quality, or returns — those are
          governed by the third-party retailer's own terms.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>8. Customer Responsibilities</h2>
          <ul className="list-disc pl-6 space-y-1 text-sm leading-relaxed">
            <li>Provide safe and lawful access to the service address at the scheduled time</li>
            <li>Disclose known hazards (pets, structural issues, hazardous materials) in advance</li>
            <li>Ensure an adult (18+) is present or authorize us to enter unattended</li>
            <li>Pay all valid invoices within the terms stated on the invoice</li>
          </ul>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>9. Reviews &amp; User Content</h2>
          <p>By submitting a review, photo, or comment through the Service, you grant us a
          royalty-free, non-exclusive, worldwide license to display, reproduce, and use it for marketing
          purposes. Reviews must be honest and based on actual service received. We moderate reviews
          and may decline or remove content that violates these Terms.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>10. Prohibited Uses</h2>
          <p>You agree not to (a) use the Service for any unlawful purpose; (b) submit false or misleading
          information; (c) attempt to bypass rate limits, scrape data, or attack our systems; or (d)
          impersonate another person.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>11. Limitation of Liability</h2>
          <p>To the maximum extent permitted by law, our total liability arising from or related to the
          Service — whether in contract, tort, warranty, or otherwise — shall not exceed the amount you
          paid us for the specific service that gave rise to the claim. We are not liable for indirect,
          incidental, consequential, or punitive damages (including lost profits, lost data, or business
          interruption). We carry general liability insurance in accordance with Louisiana law.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>12. Indemnification</h2>
          <p>You agree to indemnify and hold {BUSINESS.name} harmless from any third-party claim arising
          out of your misuse of the Service, your violation of these Terms, or your violation of any
          applicable law.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>13. Governing Law &amp; Disputes</h2>
          <p>These Terms are governed by the laws of the State of {GOVERNING_STATE}, without regard to
          conflict-of-law principles. Any dispute shall first be attempted to be resolved by direct
          negotiation. If that fails, disputes shall be resolved in the state or federal courts sitting
          in {GOVERNING_STATE}, and you consent to their personal jurisdiction.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>14. Changes to These Terms</h2>
          <p>We may update these Terms from time to time. Material changes will be posted here with a new
          "Last updated" date. Your continued use of the Service constitutes acceptance of the updated
          Terms.</p>

          <h2 style={{ fontFamily: "Cabinet Grotesk" }}>15. Contact</h2>
          <address className="not-italic text-sm leading-relaxed">
            <strong>{BUSINESS.name}</strong><br />
            Southern Louisiana, USA<br />
            Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--ji-primary)] underline">{CONTACT_EMAIL}</a><br />
            Phone: <a href={`tel:${BUSINESS.phones[0].tel}`} className="text-[var(--ji-primary)] underline">{BUSINESS.phones[0].display}</a>
          </address>
        </article>
      </section>
    </div>
  );
}
